import { GUEST_ID_PREFIX } from '@federa/const/auth-session';
import { byteToHex } from '@federa/const/byte-hex';
import { buildRunId, buildRunIdRandomTail } from '@federa/const/run-id-shape';

/** Minimal crypto interface for UUID generation, avoiding lib.dom coupling for Workers compatibility. */
interface CryptoLike {
	randomUUID: () => string;
	getRandomValues: <T extends ArrayBufferView>(array: T) => T;
}

// Module-scoped declare for Web Crypto API (not in lib.es2022)
declare const crypto: CryptoLike;

/** Generate a UUID from the available crypto APIs: prefers `crypto.randomUUID`, else builds a v4 from `crypto.getRandomValues`, else falls back to a timestamp-based identifier so call sites never fail when crypto is absent. */
export function generateUuid(cryptoApi: CryptoLike = crypto): string {
	if (typeof cryptoApi.randomUUID === 'function') {
		return cryptoApi.randomUUID();
	}

	if (typeof cryptoApi.getRandomValues === 'function') {
		const buffer = new Uint8Array(16);
		cryptoApi.getRandomValues(buffer);
		buffer[6] = ((buffer[6] as number) & 0x0f) | 0x40; // version 4
		buffer[8] = ((buffer[8] as number) & 0x3f) | 0x80; // variant 10
		const hex = Array.from(buffer, byteToHex);
		return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
	}

	// Last-resort sentinel to avoid crashing callers in limited environments
	return `sentinel-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

/** One FRESH uuid per member of `items`, in order -- the ids a batch insert mints for the rows it is about to write.
 *  Spelled once because the obvious `items.map(generateUuid)` is WRONG: `map` hands each member in as `cryptoApi`, so
 *  every site had to wrap it as `items.map(() => generateUuid())`, and a site that dropped the wrapper would read
 *  `randomUUID` off a row. */
export function generateUuidPerItem(items: ReadonlyArray<unknown>): string[] {
	return items.map(() => generateUuid());
}

/** A random identifier with an OPTIONAL caller-supplied prefix, e.g. `toast-<uuid>`. The prefix is a debugging affordance
 *  only -- uniqueness comes entirely from `generateUuid`, so an omitted prefix is exactly as collision-free.
 *
 *  Lives BESIDE `generateUuid` rather than in its own leaf: it is a two-line adapter over that function and nothing else,
 *  and a separate `utils/` leaf could not import it (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans utils-to-utils value
 *  imports, so the split would force either a duplicate uuid body or a home outside the floor). */
export function generateRandomId(prefix?: string): string {
	const id = generateUuid();
	return prefix ? `${prefix}${id}` : id;
}

/** A fresh GUEST subject id: `GUEST_ID_PREFIX` over a new uuid. The prefix is what keeps a guest subject from ever naming
 *  a real user's record (a `profiles.id` never carries it), so every mint -- the server's signed guest session and the
 *  browser's own fallback -- spells it here, once. Beside `generateUuid` for the floor reason `generateRandomId` records. */
export function mintGuestUserId(): string {
	return `${GUEST_ID_PREFIX}${generateUuid()}`;
}

/** Cryptographically secure random string of `length` chars (default 9), via crypto.getRandomValues. */
export function generateSecureRandomString(length: number = 9): string {
	const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
	const result = new Uint8Array(length);
	crypto.getRandomValues(result);
	const output = [];
	for (let i = 0; i < result.length; i++) {
		const byte = result[i] as number;
		const charIndex = byte % chars.length;
		output.push(chars[charIndex]);
	}
	return output.join('');
}

/** Session identifier for an activity/session-touch record: `sess-<epoch>-<random>-<user tail>`, anonymous when no user
 * id is supplied. Homed in the leaf that owns its dependency rather than one of its own: it is a single call over
 * `generateSecureRandomString` below, and a separate `utils/` home would need the cross-utils value import the floor
 * forbids. */
export function generateSessionId(userId: string | null): string {
	const randomPart = generateSecureRandomString(12);
	const userFragment = userId ? String(userId).slice(-8) : 'anon';
	return `sess-${Date.now()}-${randomPart}-${userFragment}`;
}

/** Generate a flow run ID. EVERY part of the shape -- prefix, separator, epoch width and the random tail's width --
 *  is owned by `@federa/const/run-id-shape`; this producer supplies only the clock and the entropy. `buildRunIdRandomTail`
 *  owns the tail's narrowing, never a DISPLAY cap such as `shortenChecksumForDisplay`: a display cap narrows a persisted
 *  identifier, and because a uuid groups on the same character the shape uses as its segment separator, it cuts
 *  mid-group and forges a fourth segment (`run-<epoch>-4c4da9de-d1e`). */
export function generateRunId(generateUuidFn: () => string): string {
	return buildRunId(Date.now(), buildRunIdRandomTail(generateUuidFn()));
}

/*
 * NO FLOW-SCOPED run-ID producer lives here, because no reader consumes a flow-scoped id (the analytics Steps drawer
 * keys span rows by a platform-minted `trace_id`). The SHAPE SSOT is `buildFlowScopedRunId` (`const/run-id-shape.ts`),
 * prescribed by QUAL-RUN-ID-SHAPE-INLINE-001's `fix`, so a producer that gains a reader composes it rather than
 * re-spelling the format.
 */
